import { TenantViewModel, UserInputModel, UserViewModel } from '@ail/customers-api'
import { UserContext } from '../../../../../auth/authContext'
import { useTranslation } from 'react-i18next'
import { PermissionsEnum } from '../../../../../types/enums'

interface Props {
  currentUser?: UserContext | null
  editingUser: UserInputModel
  userTenant?: TenantViewModel
  existingUser?: UserViewModel
  toggleGuestUserRole: (enabled: boolean) => void
  toggleManagerRole: (enabled: boolean) => void
  togglePermission: (enabled: boolean, permission: PermissionsEnum) => void
  setEditingUser: (value: React.SetStateAction<UserInputModel>) => void
}
export default function RolesAndPrivilegies({
  currentUser,
  editingUser,
  userTenant,
  existingUser,
  toggleGuestUserRole,
  toggleManagerRole,
  togglePermission,
  setEditingUser,
}: Props) {
  const { t } = useTranslation('common')

  return (
    <div className="col-12 col-sm-6 col-md-3">
      <div
        className="box"
        style={
          currentUser?.isAdmin || (currentUser?.isInternalUser && currentUser?.isManager)
            ? undefined
            : { pointerEvents: 'none' }
        }
      >
        <div className="itemTitle">
          <span>
            <i className="icon-roles"></i>
            {t('ROLES_AND_PRIVILEGES')}
          </span>
        </div>
        <div className="two-fa-settings">
          <label>{t('STATUS_AND_ROLE')}</label>
          <div className="switch">
            <input
              type="checkbox"
              name="Enabled"
              checked={editingUser.enabled}
              onChange={(e) => setEditingUser({ ...editingUser, enabled: e.target.checked })}
            />
            <span className="slider round"></span>
            <label className="switchLabel">{editingUser.enabled ? t('ENABLED') : t('DISABLED')}</label>
          </div>
        </div>
        <ul className="priviList">
          {!userTenant?.isSupplier && !existingUser?.isSupplier && (
            <li className={editingUser.isManager ? 'selected' : 'not-selected'}>
              <input
                checked={editingUser.isManager}
                className="hiddenInput"
                name="IsManager"
                type="checkbox"
                onChange={(e) => toggleManagerRole(e.target.checked)}
              />
              <div className="priv">
                <i className="icon-manager"></i>
                {t('MANAGER')}
              </div>
            </li>
          )}

          <li className={editingUser.isGuest ? 'selected' : 'not-selected'}>
            <input
              checked={editingUser.isGuest}
              className="hiddenInput"
              name="IsGuest"
              type="checkbox"
              onChange={(e) => toggleGuestUserRole(e.target.checked)}
            />
            <div className="priv">
              <i className="icon-help"></i>
              {t('GUEST')}
            </div>
          </li>

          {/*
                        isCustomer / isSupplier are tenant-derived role flags, locked post-creation.
                        Set once at new-user creation from userTenant; existing users trust BE data.
                        BE stores whatever FE sends — no derivation or validation server-side yet.
                      */}
          {userTenant?.isCompany && (
            <li className="selected">
              <input disabled checked className="hiddenInput" name="isCustomer" type="checkbox" readOnly />
              <div className="priv">
                <i className="icon-customers"></i>
                {t('CUSTOMER')}
              </div>
            </li>
          )}
          {userTenant?.isSupplier && (
            <li className="selected">
              <input disabled checked className="hiddenInput" name="isSupplier" type="checkbox" readOnly />
              <div className="priv">
                <i className="icon-supplier"></i>
                {t('SUPPLIER')}
              </div>
            </li>
          )}
        </ul>
        {!editingUser.isGuest && (
          <div className="two-fa-settings" style={{ marginTop: 10 }}>
            <label>{t('2FA')} </label>
            <div className="switch">
              <input
                type="checkbox"
                name="RequiresCodeAuthentication"
                checked={!!editingUser.requiresCodeAuthentication}
                onChange={(e) => setEditingUser({ ...editingUser, requiresCodeAuthentication: e.target.checked })}
              />
              <span className="slider round" />
              <label className="switchLabel">
                {editingUser.requiresCodeAuthentication ? t('ENABLED') : t('DISABLED')}
              </label>
            </div>
          </div>
        )}
        {!editingUser.isGuest && userTenant?.isSupplier && editingUser.isSupplier ? (
          <>
            <label>{t('PRIVILEGIES')}</label>
            <ul className="priviList">
              <li className={editingUser.permissions?.includes(PermissionsEnum.IncomingView) ? 'selected' : undefined}>
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.IncomingView)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.IncomingView)}
                />
                <div className="priv">
                  <i className="icon-garageIn"></i>
                  {t('INCOMING')}
                </div>
              </li>
              <li className={editingUser.permissions?.includes(PermissionsEnum.OutgoingView) ? 'selected' : undefined}>
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.OutgoingView)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.OutgoingView)}
                />
                <div className="priv">
                  <i className="icon-garageOut"></i>
                  {t('OUTGOING')}
                </div>
              </li>
              <li className={editingUser.permissions?.includes(PermissionsEnum.WorkItemsView) ? 'selected' : undefined}>
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.WorkItemsView)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.WorkItemsView)}
                />
                <div className="priv">
                  <i className="icon-requests"></i>
                  {t('WORK_ITEMS')}
                </div>
              </li>
            </ul>
          </>
        ) : null}
        {!editingUser.isGuest && !userTenant?.isSupplier && !editingUser.isSupplier && (
          <>
            <div className="separator"></div>
            <label>{t('PRIVILEGIES')}</label>
            <ul className="priviList">
              <li
                className={
                  editingUser.permissions && editingUser.permissions.includes(PermissionsEnum.ReportsView)
                    ? 'selected'
                    : undefined
                }
              >
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.ReportsView)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.ReportsView)}
                />
                <div className="priv">
                  <i className="icon-report"></i>
                  {t('VIEW_REPORTS')}
                </div>
              </li>
              <li
                className={
                  editingUser.permissions && editingUser.permissions.includes(PermissionsEnum.RequestsApprove)
                    ? 'selected'
                    : undefined
                }
              >
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.RequestsApprove)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.RequestsApprove)}
                />
                <div className="priv">
                  <i className="icon-requests"></i>
                  {t('APPROVE_REQUEST')}
                </div>
              </li>
              <li className={editingUser.permissions?.includes(PermissionsEnum.OrdersManage) ? 'selected' : undefined}>
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.OrdersManage)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.OrdersManage)}
                />
                <div className="priv">
                  <i className="icon-requests-man"></i>
                  {t('MANAGE_REQUESTS')}
                </div>
              </li>
              <li
                className={
                  editingUser.permissions && editingUser.permissions.includes(PermissionsEnum.DocumentsView)
                    ? 'selected'
                    : undefined
                }
              >
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.DocumentsView)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.DocumentsView)}
                />
                <div className="priv">
                  <i className="icon-doc-folder"></i>
                  {t('VIEW_DOCUMENTS')}
                </div>
              </li>
              <li className={editingUser.permissions?.includes(PermissionsEnum.CostsView) ? 'selected' : undefined}>
                <input
                  className="hiddenInput"
                  name="Permissions"
                  type="checkbox"
                  checked={editingUser.permissions?.includes(PermissionsEnum.CostsView)}
                  onChange={(e) => togglePermission(e.target.checked, PermissionsEnum.CostsView)}
                />
                <div className="priv">
                  <i className="icon-cost"></i>
                  {t('VIEW_COSTS')}
                </div>
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
